/**
 * @fileoverview Application routing and navigation system
 * 
 * This file implements the main routing structure for the application using React Router.
 * It defines all available routes and their corresponding components, handles authentication
 * with Okta, and manages navigation between different sections of the application.
 * 
 * Key features:
 * - Protected routes requiring authentication
 * - Public routes for authentication and verification flows
 * - Dynamic page titles based on current route
 * - Session management with idle timeout detection
 * - Authentication state persistence and restoration
 */

import React, { useEffect, useContext } from 'react';
import {
  Routes,
  Route,
  useNavigate,
  useLocation
} from 'react-router-dom';
import '@eightfold.ai/octuple/lib/octuple.css';
import WelcomePage from '../pages/WelcomePage';
import WelcomeBackPage from '../pages/WelcomeBackPage';
import Onboarding from '../pages/onboarding/Onboarding';
import DashBoard from '../pages/DashBoard';
import { Security, LoginCallback } from '@okta/okta-react';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import config from './config';
import { RequiredAuth } from './RequiredAuth';
import InviteExpired from '../pages/InviteExpired';
import IdleTimer from '../IdleTime';
import { ShowLogout } from '../commons/commonFunctions';
import InviteRaters from '../pages/raters/InviteRatersNew';
import WelcomeRaterSurvey from '../pages/raters/WelcomeRaterSurvey';
import InviteVerify from '../pages/InviteVerify';
import ErrorPage from '../pages/ErrorPage';
import VerifyEmail from '../pages/VerifyEmail';

//Preview pages
import WelcomePreview from '../pages/preview/WelcomePreview';
import DashboardPreview from '../pages/preview/DashboardPreview';
import SetPassword from '../pages/SetPassword';

//Immersive Agility Pages
import Introduction from '../pages/immersive-agility/Introduction';
import IntroductoryVideo from '../pages/immersive-agility/IntroductoryVideo';

import DownloadPDF from '../pages/immersive-agility/DownloadPDF';
import PrepStepsCompletion from '../pages/immersive-agility/PrepStepsCompletion';
import { globalContext } from '../store';
import BusinessImpact  from '../pages/assessments/BusinessImpact';
import MayoSelf from '../pages/assessments/Mayo-360';


/**
 * Initialize Okta authentication instance with configuration
 * This instance is used by the Security component to manage authentication state
 */
const okatAuth = new OktaAuth(config.oidc);

/**
 * Main router component for the application
 * 
 * This component handles routing logic, dynamic page titles, and wraps
 * the application in the Okta security context. It also includes components
 * for session timeout monitoring and logout confirmation.
 * 
 * @returns {JSX.Element} The application routing structure
 */
const AppRouter = () => {
  let location = useLocation();
  let navigate = useNavigate();
  const { globalState } = useContext(globalContext);

  /**
   * Callback used by Okta to restore the original URI after authentication
   * 
   * When a user is redirected to login, this function ensures they return
   * to their original destination after successful authentication.
   * 
   * @param {OktaAuth} _okatAuth - The Okta authentication instance
   * @param {string} originalUri - The URI to redirect to after login
   */
  const restoreOriginalUri = async (_okatAuth: OktaAuth, originalUri: string) => {
    navigate(toRelativeUrl(originalUri || '', window.location.origin), {
      replace: true,
    });
  }

  /**
   * Get the client name from global state
   */
  const clientName = globalState?.clientData?.name || '';
  /**
   * Generate page title with optional client name prefix
   * @param {string} title - The base title for the page
   * @returns {string} The complete page title with client name if available
   */
  const getTitle = (title: string) => {
    return clientName ? `${title} | ${clientName}` : title;
  };

  /**
   * Mapping of route paths to page titles
   * This dictionary is used to set appropriate document titles based on the current route
   */
  const titleDictionary: any = {
    '/': getTitle('Leader Portal '),
    '/welcome': getTitle('Leader Portal'),
    '/welcome-back': getTitle('Leader Portal'),
    '/register-account': getTitle('Leader Portal'),
    '/register-role': getTitle('Leader Portal'),
    '/register-company': getTitle('Leader Portal'),
    '/register-personal': getTitle('Leader Portal'),
    '/login': getTitle('Leader Portal'),
    '/set-password': getTitle('Leader Portal'),//NOSONAR
    '/invitation-verification': getTitle('Leader Portal'),
    '/dashboard': getTitle('Leader Portal'),
    '/invite-raters': getTitle('Leader Portal'),
    '/raterSurveyWelcome': getTitle('Leader Portal'),
    '/immersive/introduction': getTitle('Leader Portal'),
    '/immersive/introductoryVideo': getTitle('Leader Portal'),
    '/immersive/downloadPDF': getTitle('Leader Portal'),
    '/immersive/prepStepsCompletion': getTitle('Leader Portal'),
    '/welcome-preview': getTitle('Leader Portal'),
    '/dashboard-preview': getTitle('Leader Portal'),
    '/error': getTitle('Leader Portal'),
  };

  /**
   * Effect to update the document title based on the current route
   * Dynamically sets the browser tab title when navigation occurs and when client name changes
   */
  useEffect(() => {
    let currentPath = location.pathname;
    document.title = titleDictionary[currentPath] || getTitle('Leader Portal');
  }, [location, clientName]);

  return (
    <Security
      oktaAuth={okatAuth}
      restoreOriginalUri={restoreOriginalUri}
    >
      <Routes>
        {/* Protected routes that require authentication */}
        <Route element={<RequiredAuth />}>
          {/* Default route - catch all unmatched paths */}
          <Route path="*" element={<WelcomePage />} />

          {/* Main application routes */}
          <Route path='/welcome' element={<WelcomePage />} />
          <Route path='/welcome-back' element={<WelcomeBackPage />} />

          {/* Onboarding registration flow routes */}
          <Route path='/register-account' element={<Onboarding />} />
          <Route path='/register-role' element={<Onboarding />} />
          <Route path='/register-company' element={<Onboarding />} />
          <Route path='/register-personal' element={<Onboarding />} />

          {/* Main dashboard and functionality */}
          <Route path='/dashboard' element={<DashBoard />} />
          <Route path='/invite-raters' element={<InviteRaters />} />
          <Route path='/raterSurveyWelcome' element={<WelcomeRaterSurvey />} />

          {/* Immersive Agility Prep Steps */}
          <Route path='/immersive/introduction' element={<Introduction />} />
          <Route path='/immersive/introductoryVideo' element={<IntroductoryVideo />} />
          <Route path='/immersive/downloadPDF' element={<DownloadPDF />} />
          <Route path='/immersive/prepStepsCompletion' element={<PrepStepsCompletion />} />

          {/* Business Impact in house */}
          <Route path='/business-impact/:id' element={<BusinessImpact />} />
          <Route path='/assessment/:id' element={<MayoSelf />} />

          {/* Error handling */}
          <Route path='/error' element={<ErrorPage />} />
        </Route>

        {/* Authentication callback handler */}
        <Route
          path={'/login/callback'}
          element={
            <LoginCallback
              errorComponent={() => {
                // Clean up Okta storage items on authentication error
                localStorage.removeItem('okta-original-uri-storage');
                localStorage.removeItem('okta-cache-storage');
                localStorage.removeItem('okta-shared-transaction-storage');
                localStorage.removeItem('okta-token-storage');
                navigate('/');
                return null;
              }}
            />
          }
        />

        {/* Public routes accessible without authentication */}
        <Route
          path={'/invite-expired'}
          element={<InviteExpired />}
        />
        <Route
          path={'/invitation-verification'}
          element={<InviteVerify />}
        />
        <Route
          path={'/verify-email'}
          element={<VerifyEmail />}
        />
        <Route
          path={'/set-password'}
          element={<SetPassword />}
        />

        {/* Preview routes for demonstration purposes */}
        <Route
          path={'/welcome-preview'}
          element={<WelcomePreview />}
        />
        <Route
          path={'/dashboard-preview'}
          element={<DashboardPreview />}
        />
      </Routes>

      {/* Global components rendered on all pages */}
      <IdleTimer /> {/* Monitors user activity and handles session timeout */}
      <ShowLogout /> {/* Displays logout confirmation modal when triggered */}
    </Security>
  )
}

/**
 * Export the AppRouter component as the default export
 * This component is imported and used in App.tsx as the main application entry point
 */
export default AppRouter;